import type { Disposer } from '@elara/core/types';
import type { ItineraryItemEvent } from '@elara/domain/models/schemas/itinerary-item-event-schemas';
import type { BookingDetails, ItineraryItemType } from '@elara/domain/models/schemas/itinerary-item-schemas';
import type { Cost } from '@elara/domain/models/schemas/shared-schemas';
import type { ShortlistItemLocation } from '@elara/domain/models/schemas/shortlist-schemas';

import type { DestinationsContainer } from '#containers/DestinationsContainer.js';
import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { HotelsContainer } from '#containers/HotelsContainer.js';
import type { ItineraryContainer } from '#containers/ItineraryContainer.js';
import type { AppFlowsContainer } from '#flows.js';
import type { RoomCountStep } from '#lib/room-count-picker.js';
import type {
  DayPlaceholders,
  DestinationDescriptor,
  HotelStayInfo,
  PlannedDestination,
} from '#lib/itinerary-utils.js';
import type { Destination } from '#models/DestinationModel.js';
import type { Hotel } from '#models/HotelModel.js';
import type { ItineraryItem } from '#models/ItineraryItemModel.js';
import type { QueryHandle } from '../firebase/types.js';
import type { ToastState } from './ToastState.js';
import type { TripsState } from './TripsState.js';

import { effect } from '@preact/signals-react';
import { addDays, differenceInDays, parseISO } from 'date-fns';

import { INSUFFICIENT_INVENTORY_CODE } from '@elara/domain/constants';
import { isCancelledBooking } from '@elara/domain/models/itinerary-expansion';
import { where } from '@elara/firebolt/shared';

import { trackItineraryItemAdded, trackItineraryItemDeleted, trackRoomCountChanged } from '#lib/analytics-events.js';
import { extractDatePart, formatDate } from '#lib/date-utils.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { FlowHttpError, isOfflineFlowError, OFFLINE_FLOW_ERROR_MESSAGE } from '#lib/flow-utils.js';
import {
  compareEvents,
  computeDestinationGroups,
  computeGroupPlaceholders,
  computeHotelStaysByDay,
  createGroupedHotelEvents,
} from '#lib/itinerary-utils.js';
import { metadataMatches } from '#lib/occupancy-utils.js';
import { isAddedRoom, newestAddedRoom, roomCountKey } from '#lib/room-count-picker.js';
import { createState } from '#lib/signals-utils.js';

/** Stable empty query for itinerary items when no trip is selected (isReady: true so no loading spinner). */
const EMPTY_ITINERARY_QUERY = createState({
  key: 'empty-itinerary',
  isReady: true,
  fromCache: false,
  items: [] as ItineraryItem[],
  unsubscribe: () => {},
});

/**
 * Input data for adding a new itinerary item
 */
export interface AddItineraryItemInput {
  type: ItineraryItemType;
  title: string;
  description?: string;
  referenceId?: string;
  referenceData?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  /** Optional; server resolves from flight/room when omitted */
  startDateTime?: string;
  endDateTime?: string;
  location?: ShortlistItemLocation;
  cost?: Cost;
  booking?: BookingDetails;
  notes?: string;
  attachments?: string[];
  /**
   * For room items, number of identical rooms to create (1–10). Each room is
   * its own itinerary item; this field is consumed by the AddItineraryItem
   * flow as a top-level parameter and is NOT stored on the resulting items.
   */
  quantity?: number;
}

/**
 * A destination group ready for display: the resolved label + date window, the
 * trip days it covers, and per-currency cost subtotals. Produced by the
 * `destinationGroups` getter from the shared engine's `DestinationGroup`.
 */
export interface ItineraryDestinationGroup {
  /** Resolved display label (destination name, else trip destinations, else "Your trip"). */
  label: string;
  /** Cover image for the destination, when its catalog entry has one — used for the group header banner. */
  coverImageUrl?: string;
  descriptor?: DestinationDescriptor;
  /** The trip days belonging to this group, in order, each carrying precomputed placeholder flags. */
  days: Array<{ dayNumber: number; date: Date; formattedDate: string; placeholders: DayPlaceholders }>;
  startDateKey: string;
  endDateKey: string;
  /** Per-currency cost subtotals; the view formats via formatPrice. */
  costs: Cost[];
  /** Whether to show the "Choose your transportation" section above this group's days. */
  showTransportationSection: boolean;
}

/** One rendered calendar day of a trip's itinerary. */
export interface ItineraryDay {
  dayNumber: number;
  date: Date;
  formattedDate: string;
}

/**
 * The calendar days an itinerary spans: the trip's own date range widened to cover every event on it.
 * A trip whose dates were never set still renders its whole itinerary (the events themselves pin the
 * range), and a trip with dates narrower than what's been planned doesn't hide the overflow.
 *
 * The single derivation behind both the current-trip `tripDays` getter (web's builder) and
 * `destinationGroupsForTrip` (the mobile itinerary screen) — the mobile screen used to require
 * explicit trip dates and rendered "No itinerary yet" for a dateless trip that had a full itinerary.
 *
 * Returns `[]` when neither the trip nor its events pin a date, and when the range is inverted.
 */
function resolveItineraryDays(
  tripStartDate: string | undefined,
  tripEndDate: string | undefined,
  events: ItineraryItemEvent[],
): ItineraryDay[] {
  // Compare on date keys, not raw values: a trip date may be a full ISO datetime while an event date
  // is already date-only, and `'2026-07-01' < '2026-07-01T00:00:00Z'` lexically.
  let startKey = tripStartDate ? (extractDatePart(tripStartDate) ?? tripStartDate) : undefined;
  let endKey = tripEndDate ? (extractDatePart(tripEndDate) ?? tripEndDate) : undefined;

  const eventKeys = events
    .map((event) => extractDatePart(event.dateTime))
    .filter((datePart): datePart is string => !!datePart)
    .toSorted();
  const earliestEventKey = eventKeys[0];
  const latestEventKey = eventKeys.at(-1);
  if (earliestEventKey && (!startKey || earliestEventKey < startKey)) startKey = earliestEventKey;
  if (latestEventKey && (!endKey || latestEventKey > endKey)) endKey = latestEventKey;

  if (!startKey || !endKey) return [];

  const start = parseISO(startKey);
  const end = parseISO(endKey);
  const totalDays = differenceInDays(end, start) + 1;
  if (totalDays <= 0) return [];

  return Array.from({ length: totalDays }, (_, index) => {
    const date = addDays(start, index);
    return { dayNumber: index + 1, date, formattedDate: formatDate(date, 'EEEE, MMMM do') };
  });
}

/**
 * "Closing" events not worth featuring as the next thing happening — a flight arrival, hotel checkout,
 * or layover wraps something up rather than kicking it off, so the banner skips them (ELARA-870).
 *
 * `destination_arrival` is here for a second reason too: it is an internal transition event the
 * itinerary itself never renders (see `HIDDEN_EVENT_TYPES`), and a flight that crosses a border emits
 * one alongside its departure. Featuring both put the same flight on the overview's rail twice.
 */
const NON_FEATURED_NEXT_EVENT_TYPES = new Set<ItineraryItemEvent['type']>([
  'flight_arrival',
  'destination_arrival',
  'hotel_checkout',
  'layover',
]);

/**
 * Expand itinerary items into the chronologically-sorted event list (room items grouped into hotel
 * stays, positioned relative to flights). Shared by the current-trip `events` getter and the
 * arbitrary-trip `nextEventForTrip` (the trips-page active-trip banner).
 */
function expandItemsToSortedEvents(items: ItineraryItem[]): ItineraryItemEvent[] {
  const roomItems = items.filter((i) => i.type === 'room');
  const nonRoomItems = items.filter((i) => i.type !== 'room');
  const nonRoomEvents = nonRoomItems.flatMap((item) => item.expandToEvents());
  const flightEvents = nonRoomEvents.filter((e) => e.type === 'flight_departure' || e.type === 'flight_arrival');
  const roomEvents = createGroupedHotelEvents(roomItems, flightEvents);
  return [...nonRoomEvents, ...roomEvents].toSorted((a, b) => compareEvents(a, b)) as ItineraryItemEvent[];
}

/**
 * State management for itinerary items
 */
/** Outcome of `updateItemNotes`: saved, or not and why. */
export type UpdateItemNotesResult = { ok: true } | { ok: false; message: string };

export function createItineraryState(
  tripsState: TripsState,
  flows: AppFlowsContainer,
  destinations: DestinationsContainer,
  hotels: HotelsContainer,
  itinerary: ItineraryContainer,
  firestore: FirestoreContainer,
  toastState: ToastState,
) {
  const { ClientDestination } = destinations;
  const { ClientHotel } = hotels;
  const { ClientItineraryItem } = itinerary;

  // Cache for destination results by country code to avoid repeated computation
  const destinationResultCache = new Map<string, Destination | undefined>();
  const state = createState({
    /** Track previous trip ID to detect trip changes for query cleanup */
    _previousTripId: undefined as string | undefined,
    /** Previous items query handle for cleanup on trip switch */
    _prevItemsQuery: undefined as QueryHandle<ItineraryItem> | undefined,
    /** Collection of cleanup functions for all effects */
    cleanupFunctions: [] as Disposer[],
    /** Cleanup function reference to ensure idempotency */
    cleanupFunctionRef: null as Disposer | null,

    /** Whether the delete itinerary item dialog is open */
    isDeleteDialogOpen: false,
    /** The itinerary item to delete */
    itemToDelete: null as ItineraryItem | null,
    /** Whether the booking details dialog is open */
    isBookingDialogOpen: false,
    /** The itinerary item to edit booking for */
    itemForBookingDialog: null as ItineraryItem | null,
    /** Whether the read-only "view booking" dialog is open */
    isViewBookingDialogOpen: false,
    /** The single booked item whose details are shown in the view dialog */
    viewBookingItem: null as ItineraryItem | null,
    /** True while GenerateItinerarySummaries flow is in progress */
    isGeneratingSummaries: false,
    /**
     * Room count pickers with a step in flight, by `roomCountKey`, and which step: their buttons wait for it,
     * and the pressed one spins.
     */
    roomCountSteps: {} as Record<string, RoomCountStep>,

    /** Reactive query for itinerary items of the current trip; empty handle when no trip selected. */
    get itemsQuery() {
      const tripId = tripsState.current?.id;
      if (!tripId) return EMPTY_ITINERARY_QUERY;
      return ClientItineraryItem.list({ tripId });
    },

    /** Whether the itinerary items query has resolved (or no trip selected). */
    get itemsReady(): boolean {
      return this.itemsQuery.isReady;
    },

    /**
     * Whether the items query AND every item's referenced docs (flight options,
     * room options + hotels, places) have resolved. Destination grouping only
     * stabilizes once references load, so the builder holds its list render until
     * this is true to avoid groups visibly reshuffling as data streams in (ELARA-836).
     */
    get isFullyLoaded(): boolean {
      try {
        if (!this.itemsReady) return false;
        return this.items.every((item) => item.referencesReady);
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'isFullyLoaded' },
          extra: { tripId: tripsState.current?.id },
        });
        // Fail open: don't trap the UI on a loader if readiness computation throws.
        return true;
      }
    },

    /** Get itinerary items for the current trip */
    get items(): ItineraryItem[] {
      try {
        const query = this.itemsQuery;
        return (query.items ?? []) as ItineraryItem[];
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'createItineraryItemsQuery' },
          extra: { tripId: tripsState.current?.id },
        });
        return [];
      }
    },

    /** Get expanded itinerary events for display */
    get events(): ItineraryItemEvent[] {
      try {
        return expandItemsToSortedEvents(this.items);
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'getItineraryEvents' },
          extra: { tripId: tripsState.current?.id },
        });
        return [];
      }
    },

    /**
     * The next upcoming event for an ARBITRARY trip (not necessarily the current one), based on the
     * user's local time — the first event whose start is at or after now. Reactive (subscribes to that
     * trip's itinerary query). Used by the trips-page active-trip banner; returns the event + its source
     * item, or `undefined` when the trip has no upcoming events.
     */
    /**
     * Everything still to come TODAY on an arbitrary trip, soonest first.
     *
     * "Still to come" is deliberately generous about all-day events: they count for the whole day rather
     * than expiring at midnight-plus-one, since an all-day entry is a fact about the day and not an
     * appointment that can be missed. Timed events drop out once they have started — a dinner at 7 is
     * not "next up" at 8.
     *
     * Today only. A future day's event is not what someone mid-trip is asking about, and offering one
     * would make the module read as a plan rather than a next step.
     *
     * Reactive — subscribes lazily on read.
     */
    upcomingEventsForTrip(tripId: string): { event: ItineraryItemEvent; item: ItineraryItem | undefined }[] {
      try {
        const items = (ClientItineraryItem.list({ tripId }).items ?? []) as ItineraryItem[];
        const sorted = expandItemsToSortedEvents(items);
        const now = new Date();
        const nowMs = now.getTime();
        // The user's LOCAL day — a UTC day boundary would drop this evening's dinner for anyone west of
        // UTC, and surface tomorrow's for anyone east.
        const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        return (
          sorted
            .filter((event) => {
              if (NON_FEATURED_NEXT_EVENT_TYPES.has(event.type)) return false;
              // A cancelled booking is not something the traveller is about to do. Cancelled hotel rooms
              // never reach the events (the engine leaves them out of the itinerary); this keeps any other
              // cancelled booking, such as a flight, from being featured (ELARA-972).
              if (isCancelledBooking(event)) return false;
              if (extractDatePart(event.dateTime) !== todayKey) return false;
              const isAllDay = /^\d{4}-\d{2}-\d{2}$/.test(event.dateTime);
              return isAllDay || new Date(event.dateTime).getTime() >= nowMs;
            })
            // A connecting flight contributes one entry per leg, which is right: each is a flight the
            // traveller boards, and each card shows its own leg (see `lib/next-up`).
            .map((event) => ({ event, item: items.find((i) => i.id === event.itemId) }))
        );
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'upcomingEventsForTrip' },
          extra: { tripId },
        });
        return [];
      }
    },

    /**
     * The soonest of {@link upcomingEventsForTrip} — the home banner's "Next up" band, which has room
     * for exactly one.
     */
    nextEventForTrip(tripId: string): { event: ItineraryItemEvent; item: ItineraryItem | undefined } | undefined {
      return this.upcomingEventsForTrip(tripId)[0];
    },

    /**
     * All itinerary items for an ARBITRARY trip. Reactive — subscribes lazily on read, following the
     * same pattern as `nextEventForTrip`. Returns `[]` while the query is loading.
     */
    itemsForTrip(tripId: string): ItineraryItem[] {
      try {
        return (ClientItineraryItem.list({ tripId }).items ?? []) as ItineraryItem[];
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'itemsForTrip' },
          extra: { tripId },
        });
        return [];
      }
    },

    /**
     * Whether an ARBITRARY trip's itinerary query has resolved — the `itemsReady` counterpart to
     * {@link itemsForTrip}, so a screen driven by a route param (mobile, where `tripsState.current`
     * isn't route-owned) can tell "still loading" from "genuinely empty" instead of flashing an
     * empty state. Reactive; fails open (treats a throwing query as resolved) so a broken read
     * surfaces the empty state rather than trapping the screen on a spinner.
     */
    itemsReadyForTrip(tripId: string): boolean {
      try {
        return ClientItineraryItem.list({ tripId }).isReady;
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'itemsReadyForTrip' },
          extra: { tripId },
        });
        return true;
      }
    },

    /**
     * The full chronologically-sorted event list for an ARBITRARY trip (room items grouped into hotel
     * stays, positioned relative to flights) — the same expansion the current-trip `events` getter and
     * the itinerary builder use, exposed for any trip id. Follows the `itemsForTrip`/`nextEventForTrip`
     * pattern so the mobile event-detail screen can resolve the exact event a tapped row rendered
     * (including grouped hotel check-ins) without re-deriving the grouping itself. Returns `[]` while
     * items are loading.
     */
    eventsForTrip(tripId: string): ItineraryItemEvent[] {
      try {
        const items = (ClientItineraryItem.list({ tripId }).items ?? []) as ItineraryItem[];
        return expandItemsToSortedEvents(items);
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'eventsForTrip' },
          extra: { tripId },
        });
        return [];
      }
    },

    /**
     * Destination-grouped itinerary for an ARBITRARY trip (not necessarily `tripsState.current`).
     * Follows the `nextEventForTrip` pattern — queries items by tripId directly so the screen works
     * on mobile where trip-change monitors are not started. Returns `[]` while items are loading, and
     * once loaded only when neither the trip's dates nor its events pin a date range.
     */
    destinationGroupsForTrip(tripId: string): ItineraryDestinationGroup[] {
      try {
        const trip = tripsState.recentTrips.items.find((t) => t.id === tripId);
        if (!trip) return [];

        const items = (ClientItineraryItem.list({ tripId }).items ?? []) as ItineraryItem[];
        const allEvents = expandItemsToSortedEvents(items);

        // The trip's date range widened to cover every event — the same derivation the current-trip
        // `tripDays` getter (web's builder) uses, so a trip whose dates were never set still renders
        // its itinerary here instead of the "No itinerary yet" empty state.
        const days = resolveItineraryDays(trip.startDate ?? undefined, trip.endDate ?? undefined, allEvents);
        if (days.length === 0) return [];

        const dayByKey = new Map(days.map((day) => [formatDate(day.date, 'yyyy-MM-dd'), day]));
        const orderedDateKeys = [...dayByKey.keys()];

        const plan: PlannedDestination[] = (trip.destinationPlan ?? []).flatMap((entry) => {
          const startKey = entry.startDate ? extractDatePart(entry.startDate) : undefined;
          const endKey = entry.endDate ? extractDatePart(entry.endDate) : undefined;
          if (!startKey || !endKey) return [];
          const dest = this.getDestinationById(entry.destinationId);
          const name = entry.name ?? dest?.name ?? undefined;
          const descriptor: DestinationDescriptor = {
            destinationId: entry.destinationId,
            ...(name ? { name } : {}),
            ...(dest?.name ? { cityName: dest.name } : {}),
            ...(dest?.location?.countryCode ? { countryCode: dest.location.countryCode } : {}),
            ...(dest?.location?.countryName ? { countryName: dest.location.countryName } : {}),
          };
          return [{ descriptor, startDateKey: startKey, endDateKey: endKey }];
        });

        const groups = computeDestinationGroups(items, orderedDateKeys, plan);

        // Build eventsByDay and hotelStaysByDay so computeGroupPlaceholders can evaluate
        // per-day placeholder rules — mirrors the current-trip getter logic.
        const eventsByDay: Record<string, ItineraryItemEvent[]> = {};
        for (const dateKey of orderedDateKeys) {
          eventsByDay[dateKey] = [];
        }
        for (const event of allEvents) {
          const datePart = extractDatePart(event.dateTime);
          if (!datePart) continue;
          if (!eventsByDay[datePart]) eventsByDay[datePart] = [];
          eventsByDay[datePart]!.push(event);
        }
        for (const dateKey in eventsByDay) {
          eventsByDay[dateKey] = eventsByDay[dateKey]!.toSorted((a, b) => compareEvents(a, b));
        }
        const roomItems = items.filter((i) => i.type === 'room');
        const hotelStaysByDay = computeHotelStaysByDay(roomItems);
        const groupPlaceholders = computeGroupPlaceholders(groups, eventsByDay, hotelStaysByDay);
        const NO_PLACEHOLDERS: DayPlaceholders = {
          showActivitiesPlaceholder: false,
          showHotelPlaceholder: false,
          showDiningPlaceholder: false,
        };

        return groups.map((group, groupIndex): ItineraryDestinationGroup => {
          const descriptor = group.descriptor;
          const destination = descriptor?.destinationId
            ? this.getDestinationById(descriptor.destinationId)
            : descriptor?.countryCode
              ? this.getDestinationForArrival({ countryCode: descriptor.countryCode, city: descriptor.cityName })
              : undefined;
          const label = descriptor?.name ?? destination?.name ?? trip.formattedDestinations ?? 'Your trip';
          const placeholders = groupPlaceholders[groupIndex] ?? {
            showTransportationSection: false,
            dayPlaceholders: {},
          };
          return {
            label,
            ...(destination?.imageUrl ? { coverImageUrl: destination.imageUrl } : {}),
            ...(descriptor ? { descriptor } : {}),
            days: group.dateKeys
              .map((key) => {
                const day = dayByKey.get(key);
                if (!day) return undefined;
                return { ...day, placeholders: placeholders.dayPlaceholders[key] ?? NO_PLACEHOLDERS };
              })
              .filter((d): d is NonNullable<typeof d> => !!d),
            startDateKey: group.startDateKey,
            endDateKey: group.endDateKey,
            costs: group.costs,
            showTransportationSection: placeholders.showTransportationSection,
          };
        });
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'destinationGroupsForTrip' },
          extra: { tripId },
        });
        return [];
      }
    },

    /** Generate trip days based on actual itinerary events and official dates */
    get tripDays(): ItineraryDay[] {
      try {
        return resolveItineraryDays(
          tripsState.current?.startDate ?? undefined,
          tripsState.current?.endDate ?? undefined,
          this.events,
        );
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'generateTripDays' },
          extra: {
            tripId: tripsState.current?.id,
            startDate: tripsState.current?.startDate,
            endDate: tripsState.current?.endDate,
          },
        });
        return [];
      }
    },

    /** Get itinerary events grouped by day */
    get eventsByDay(): Record<string, ItineraryItemEvent[]> {
      try {
        const grouped: Record<string, ItineraryItemEvent[]> = {};
        const days = this.tripDays;
        const events = this.events;

        for (const day of days) {
          const dateKey = formatDate(day.date, 'yyyy-MM-dd');
          grouped[dateKey] = [];
        }

        for (const event of events) {
          const datePart = extractDatePart(event.dateTime);
          if (!datePart) continue;

          if (!grouped[datePart]) {
            grouped[datePart] = [];
          }
          grouped[datePart]!.push(event);
        }

        for (const dateKey in grouped) {
          grouped[dateKey] = grouped[dateKey]!.toSorted((a, b) => compareEvents(a, b));
        }

        return grouped;
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'getItineraryEventsByDay' },
          extra: { tripId: tripsState.current?.id },
        });
        return {};
      }
    },

    /** Get hotel stay info grouped by day, for the itinerary builder banners. */
    get hotelStaysByDay(): Record<string, HotelStayInfo[]> {
      try {
        const roomItems = this.items.filter((i) => i.type === 'room');
        return computeHotelStaysByDay(roomItems);
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'getHotelStaysByDay' },
          extra: { tripId: tripsState.current?.id },
        });
        return {};
      }
    },

    /**
     * Itinerary days grouped into destinations with date windows and per-currency
     * cost subtotals (ELARA-836). Hotels anchor each day, flights label transit days,
     * and the trip's working-memory-derived dated `destinationPlan` covers days with
     * no booking yet. Each group is labeled from its resolved destination, falling
     * back to the trip's destination(s) and finally "Your trip".
     */
    get destinationGroups(): ItineraryDestinationGroup[] {
      try {
        const tripDays = this.tripDays;
        if (tripDays.length === 0) return [];

        const trip = tripsState.current;
        const dayByKey = new Map(tripDays.map((day) => [formatDate(day.date, 'yyyy-MM-dd'), day]));
        const orderedDateKeys = [...dayByKey.keys()];

        // Build the dated destination plan from the trip doc (synced from working memory).
        const plan: PlannedDestination[] = (trip?.destinationPlan ?? []).flatMap((entry) => {
          const startDateKey = entry.startDate ? extractDatePart(entry.startDate) : undefined;
          const endDateKey = entry.endDate ? extractDatePart(entry.endDate) : undefined;
          if (!startDateKey || !endDateKey) return [];
          const dest = this.getDestinationById(entry.destinationId);
          const name = entry.name ?? dest?.name ?? undefined;
          const descriptor: DestinationDescriptor = {
            destinationId: entry.destinationId,
            ...(name ? { name } : {}),
            ...(dest?.name ? { cityName: dest.name } : {}),
            ...(dest?.location?.countryCode ? { countryCode: dest.location.countryCode } : {}),
            ...(dest?.location?.countryName ? { countryName: dest.location.countryName } : {}),
          };
          return [{ descriptor, startDateKey, endDateKey }];
        });

        const groups = computeDestinationGroups(this.items, orderedDateKeys, plan);
        const groupPlaceholders = computeGroupPlaceholders(groups, this.eventsByDay, this.hotelStaysByDay);

        return groups.map((group, groupIndex): ItineraryDestinationGroup => {
          const descriptor = group.descriptor;
          // Enrich with a full Destination doc when we can (id-anchored, else by arrival country).
          const destination = descriptor?.destinationId
            ? this.getDestinationById(descriptor.destinationId)
            : descriptor?.countryCode
              ? this.getDestinationForArrival({ countryCode: descriptor.countryCode, city: descriptor.cityName })
              : undefined;
          const label = descriptor?.name ?? destination?.name ?? trip?.formattedDestinations ?? 'Your trip';
          const placeholders = groupPlaceholders[groupIndex]!;
          const NO_PLACEHOLDERS: DayPlaceholders = {
            showActivitiesPlaceholder: false,
            showHotelPlaceholder: false,
            showDiningPlaceholder: false,
          };
          return {
            label,
            ...(destination?.imageUrl ? { coverImageUrl: destination.imageUrl } : {}),
            ...(descriptor ? { descriptor } : {}),
            days: group.dateKeys
              .map((key) => {
                const day = dayByKey.get(key);
                if (!day) return undefined;
                return { ...day, placeholders: placeholders.dayPlaceholders[key] ?? NO_PLACEHOLDERS };
              })
              .filter((d): d is NonNullable<typeof d> => !!d),
            startDateKey: group.startDateKey,
            endDateKey: group.endDateKey,
            costs: group.costs,
            showTransportationSection: placeholders.showTransportationSection,
          };
        });
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { module: 'ItineraryState', action: 'getDestinationGroups' },
          extra: { tripId: tripsState.current?.id },
        });
        return [];
      }
    },

    /**
     * Check if an item is in the itinerary by referenceId and type.
     * When metadata is provided, matches only if every key in metadata equals item.metadata?.[key].
     */
    isInItinerary(type: ItineraryItemType, referenceId: string, metadata?: Record<string, unknown>): boolean {
      return this.items.some((item) => {
        if (item.type !== type || item.referenceId !== referenceId) return false;
        if (metadata === undefined) return true;
        return metadataMatches(item.metadata, metadata);
      });
    },

    /** Get itinerary item by referenceId and type; optional metadata for partial match (e.g. room selectedRateKey + occupancy). */
    getItineraryItemByReference(
      type: ItineraryItemType,
      referenceId: string,
      metadata?: Record<string, unknown>,
    ): ItineraryItem | undefined {
      return this.items.find((item) => {
        if (item.type !== type || item.referenceId !== referenceId) return false;
        if (metadata === undefined) return true;
        return metadataMatches(item.metadata, metadata);
      });
    },

    /** Get all itinerary items matching the given identity (e.g. all rooms with same rate + occupancy). */
    getAllItineraryItemsByReference(
      type: ItineraryItemType,
      referenceId: string,
      metadata?: Record<string, unknown>,
    ): ItineraryItem[] {
      return this.items.filter((item) => {
        if (item.type !== type || item.referenceId !== referenceId) return false;
        if (metadata === undefined) return true;
        return metadataMatches(item.metadata, metadata);
      });
    },

    /** Get all itinerary items for a room option (type room, referenceId = roomOptionId). */
    getItineraryItemsForRoomOption(roomOptionId: string): ItineraryItem[] {
      return this.items.filter((item) => item.type === 'room' && item.referenceId === roomOptionId);
    },

    /**
     * Add a new itinerary item. Returns the created items so callers can
     * navigate using the resulting ids (e.g. "Book now" → hotel checkout).
     * Returns an empty array on failure; the user-facing toast is already
     * surfaced here, so callers can branch on `length === 0` to bail.
     */
    async addItemToItinerary(input: AddItineraryItemInput): Promise<ItineraryItem[]> {
      console.log('Adding to itinerary', input);

      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot add to itinerary',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return [];
      }

      try {
        // Build the item, only including non-null values (flight/room can omit dates; server resolves)
        const cleanedItem = {
          type: input.type,
          ...(input.startDateTime != null && input.startDateTime !== '' && { startDateTime: input.startDateTime }),
          ...(input.endDateTime != null && input.endDateTime !== '' && { endDateTime: input.endDateTime }),
          ...(input.description && { description: input.description }),
          ...(input.referenceId && { referenceId: input.referenceId }),
          ...(input.referenceData && { referenceData: input.referenceData }),
          ...(input.metadata != null && { metadata: input.metadata }),
          ...(input.location && { location: input.location }),
          ...(input.cost && { cost: input.cost }),
          ...(input.booking && { booking: input.booking }),
          ...(input.notes && { notes: input.notes }),
          ...(input.attachments && { attachments: input.attachments }),
        } as Parameters<typeof flows.addItineraryItem>[0]['item'];

        const quantity = input.type === 'room' ? input.quantity : undefined;
        const result = await flows.addItineraryItem({
          tripId,
          item: cleanedItem,
          ...(quantity != null && { quantity }),
        });
        const items = (result.data as { items?: ItineraryItem[] }).items ?? [];
        const first = items[0];
        const displayTitle = first?.title ?? 'Item';
        trackItineraryItemAdded({ tripId, itemType: input.type });
        tripsState.pulseNavIcon('itinerary');
        toastState.addToast({
          title: items.length > 1 ? `Added ${items.length} rooms to itinerary` : 'Added to itinerary',
          variant: 'success',
          description:
            items.length > 1 ? `${displayTitle} × ${items.length}` : `${displayTitle} has been added to your itinerary`,
        });
        return items;
      } catch (error) {
        console.error('[ItineraryState] Failed to add item', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to add to itinerary',
          description: 'Please try again',
          variant: 'destructive',
        });
        return [];
      }
    },

    /** The step in flight on the room count picker for this room and rate, if any. */
    roomCountStep(roomId: string, identityMeta: Record<string, unknown> | undefined): RoomCountStep | undefined {
      return this.roomCountSteps[roomCountKey(roomId, identityMeta)];
    },

    _setRoomCountStep(key: string, step: RoomCountStep | undefined): void {
      const { [key]: _previous, ...rest } = this.roomCountSteps;
      this.roomCountSteps = step ? { ...rest, [key]: step } : rest;
    },

    /**
     * Adds one room of a rate to the trip: "Add room", and plus on the room count picker. The server checks
     * the hotel can sell every planned room of this type plus this one; when it cannot, its message shows
     * as a toast. A toast rather than a line on the card, which would push that card's buttons out of line
     * with the others. The server records the limit as the room's rooms-left count, so every rate of the room
     * stops adding (`isAtStatedRoomLimit`), across reloads, until a recheck or search finds more.
     * Returns whether the room was added.
     */
    async incrementRoom(input: {
      roomId: string;
      /** The full selection the new item carries (productCode + occupancy). */
      metadata: Record<string, unknown> | undefined;
      /** The identity-narrowed metadata the picker counts rooms by. */
      identityMeta: Record<string, unknown> | undefined;
    }): Promise<boolean> {
      const tripId = tripsState.current?.id;
      const key = roomCountKey(input.roomId, input.identityMeta);
      if (!tripId || this.roomCountSteps[key]) return false;

      this._setRoomCountStep(key, 'increment');
      try {
        await flows.addItineraryItem({
          tripId,
          item: {
            type: 'room',
            referenceId: input.roomId,
            ...(input.metadata != null && { metadata: input.metadata }),
          } as Parameters<typeof flows.addItineraryItem>[0]['item'],
        });
        const added = this.getAllItineraryItemsByReference('room', input.roomId, input.identityMeta).filter(
          isAddedRoom,
        ).length;
        trackItineraryItemAdded({ tripId, itemType: 'room' });
        // The new item may not have reached the listener yet; the count after this step is at least one more.
        trackRoomCountChanged({ tripId, direction: 'increase', roomCount: added + 1 });
        tripsState.pulseNavIcon('itinerary');
        return true;
      } catch (error) {
        const details = error instanceof FlowHttpError ? (error.details as { code?: unknown } | undefined) : undefined;
        if (details?.code === INSUFFICIENT_INVENTORY_CODE) {
          toastState.addToast({ title: (error as FlowHttpError).message, variant: 'destructive' });
          return false;
        }
        if (!isOfflineFlowError(error)) getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Could not add the room',
          description: isOfflineFlowError(error) ? OFFLINE_FLOW_ERROR_MESSAGE : 'Please try again',
          variant: 'destructive',
        });
        return false;
      } finally {
        this._setRoomCountStep(key, undefined);
      }
    },

    /**
     * Removes the newest added (planned) room of a rate: minus on the room count picker. Silent, with no
     * confirmation, including the last one — re-adding is one tap. Booked rooms are never removed here.
     * Returns whether a room was removed.
     */
    async decrementRoom(input: {
      roomId: string;
      identityMeta: Record<string, unknown> | undefined;
    }): Promise<boolean> {
      const tripId = tripsState.current?.id;
      const key = roomCountKey(input.roomId, input.identityMeta);
      if (!tripId || this.roomCountSteps[key]) return false;
      const rooms = this.getAllItineraryItemsByReference('room', input.roomId, input.identityMeta);
      const target = newestAddedRoom(rooms);
      if (!target) return false;

      this._setRoomCountStep(key, 'decrement');
      try {
        await flows.deleteItineraryItem({ tripId, itemId: target.id });
        trackItineraryItemDeleted({ tripId, itemType: 'room' });
        trackRoomCountChanged({ tripId, direction: 'decrease', roomCount: rooms.filter(isAddedRoom).length - 1 });
        return true;
      } catch (error) {
        if (!isOfflineFlowError(error)) getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Could not remove the room',
          description: isOfflineFlowError(error) ? OFFLINE_FLOW_ERROR_MESSAGE : 'Please try again',
          variant: 'destructive',
        });
        return false;
      } finally {
        this._setRoomCountStep(key, undefined);
      }
    },

    /**
     * Update the start/end datetimes for an existing itinerary item.
     */
    async updateItemDates(itemId: string, payload: { startDateTime: string; endDateTime?: string }): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot update itinerary item',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return;
      }

      try {
        await flows.updateItineraryItem({
          tripId,
          itemId,
          updates: {
            startDateTime: payload.startDateTime,
            ...(payload.endDateTime && { endDateTime: payload.endDateTime }),
          },
        });

        toastState.addToast({
          title: 'Itinerary updated',
          description: 'The time for this item has been updated.',
          variant: 'success',
        });
      } catch (error) {
        console.error('[ItineraryState] Failed to update item dates', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to update time',
          description: 'Please try again.',
          variant: 'destructive',
        });
      }
    },

    /**
     * Update the personal note on an itinerary item.
     * Pass an empty string to clear the note.
     *
     * Errors are surfaced here (toast + Sentry) and never re-thrown; the boolean
     * result reports success so callers can decide whether to close their editor
     * (e.g. keep the note sheet open on failure so the user can retry).
     */
    /**
     * Saves (or, with blank `notes`, removes) an item's personal note. Never throws: the result says
     * whether it worked, and why not. A failure is toasted unless `toastOnFailure` is false — for a
     * caller that shows it inline instead, such as a sheet, which would otherwise hide the toast
     * behind itself. `tripId` defaults to the current trip; pass the item's own when the caller can
     * outlive a trip switch (a detail screen), so the note can't miss or target the wrong trip.
     */
    async updateItemNotes(
      itemId: string,
      notes: string,
      { toastOnFailure = true, tripId: itemTripId }: { toastOnFailure?: boolean; tripId?: string } = {},
    ): Promise<UpdateItemNotesResult> {
      const fail = (title: string, message: string): UpdateItemNotesResult => {
        if (toastOnFailure) toastState.addToast({ title, description: message, variant: 'destructive' });
        return { ok: false, message };
      };

      const tripId = itemTripId ?? tripsState.current?.id;
      if (!tripId) return fail('Cannot update itinerary item', 'No trip selected');

      try {
        await flows.updateItineraryItem({ tripId, itemId, updates: { notes: notes.trim() || null } });
        toastState.addToast({ title: notes.trim() ? 'Note saved' : 'Note removed', variant: 'success' });
        return { ok: true };
      } catch (error) {
        console.error('[ItineraryState] Failed to update item notes', error);
        getErrorReporter().captureException(error);
        return fail('Failed to save note', 'Please try again.');
      }
    },

    /** Open the delete itinerary item dialog */
    openDeleteDialog(item: ItineraryItem) {
      this.itemToDelete = item;
      this.isDeleteDialogOpen = true;
    },

    /** Close the delete itinerary item dialog */
    closeDeleteDialog() {
      this.isDeleteDialogOpen = false;
      this.itemToDelete = null;
    },

    /** Open the booking details dialog */
    openBookingDialog(item: ItineraryItem) {
      this.itemForBookingDialog = item;
      this.isBookingDialogOpen = true;
    },

    /** Close the booking details dialog */
    closeBookingDialog() {
      this.isBookingDialogOpen = false;
      this.itemForBookingDialog = null;
    },

    /** Open the read-only "view booking" dialog for a single booked item. */
    openViewBookingDialog(item: ItineraryItem) {
      this.viewBookingItem = item;
      this.isViewBookingDialogOpen = true;
    },

    /** Close the read-only "view booking" dialog. */
    closeViewBookingDialog() {
      this.isViewBookingDialogOpen = false;
      this.viewBookingItem = null;
    },

    /**
     * Update booking details (confirmation number, customer note, cost) and set status to booked.
     *
     * Booking updates DEEP-MERGE server-side, so key omission means "leave unchanged" — an
     * emptied field must be sent as an explicit `null` to clear the stored value (`undefined`
     * is dropped by JSON transport and would silently revert the clear).
     */
    async updateBookingDetails(
      itemId: string,
      payload: {
        confirmationNumber?: string | null;
        customerRequest?: string | null;
        actualCost?: { amount: number; currency: string } | null;
      },
    ): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot update booking',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return;
      }

      try {
        // This dialog records bookings made OUTSIDE Elara, so stamp the positive external marker
        // the server's guards key on (`DeleteItineraryItemFlow` only lets a booked room be removed
        // when `channel === 'manual'`; anything ambiguous fails closed). Only when the item has no
        // channel yet — an existing channel (e.g. a searched flight's 'direct') is kept.
        const item = this.items.find((candidate) => candidate.id === itemId);
        const updates: {
          booking: {
            status: 'booked';
            channel?: 'manual';
            confirmationNumber?: string | null;
            customerRequest?: string | null;
            actualCost?: { amount: number; currency: string } | null;
          };
        } = {
          booking: { status: 'booked', ...(item?.booking?.channel == null ? { channel: 'manual' as const } : {}) },
        };
        if (payload.confirmationNumber !== undefined) {
          updates.booking.confirmationNumber = payload.confirmationNumber?.trim() || null;
        }
        if (payload.customerRequest !== undefined) {
          updates.booking.customerRequest = payload.customerRequest?.trim() || null;
        }
        if (payload.actualCost !== undefined) {
          updates.booking.actualCost = payload.actualCost;
        }

        await flows.updateItineraryItem({ tripId, itemId, updates });
        this.closeBookingDialog();
        toastState.addToast({
          title: 'Booking updated',
          variant: 'success',
          description: 'Booking details have been saved.',
        });
      } catch (error) {
        console.error('[ItineraryState] Failed to update booking details', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to update booking',
          description: 'Please try again.',
          variant: 'destructive',
        });
        throw error;
      }
    },

    /**
     * Remove a manually-recorded booking entry. What "remove" means depends on the item
     * (`removingBookingRemovesItem`):
     *
     * - An external stay/flight (no `referenceId`) IS its booking record — the whole item is
     *   deleted, since clearing would leave an unbookable "Not booked" shell behind.
     * - Every other item keeps its place on the itinerary; its `booking` resets to `{ status:
     *   'planned' }` via `ClearBooking` (a whole-map reset flow), NOT `updateItineraryItem`:
     *   booking updates deep-merge onto the stored map, so a `{ status: 'planned' }` patch there
     *   would preserve the very fields this action exists to drop.
     *
     * Manual entries only: a Sabre-channel booking is a real PNR and must go through
     * `cancelHotelBooking` — the ClearBooking flow rejects it server-side, and
     * `item.canRemoveBooking` gates the UI to match.
     */
    async clearBookingDetails(itemId: string): Promise<boolean> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot remove booking',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return false;
      }

      const item = this.items.find((candidate) => candidate.id === itemId);
      const removesItem = item?.removingBookingRemovesItem === true;

      try {
        if (removesItem) {
          await this.deleteItem(itemId, tripId);
        } else {
          await flows.clearBooking({ tripId, itineraryItemId: itemId });
        }
        this.closeViewBookingDialog();
        toastState.addToast({
          title: 'Booking removed',
          description: removesItem
            ? 'The booking and its itinerary entry have been removed.'
            : 'The booking details have been cleared.',
          variant: 'success',
        });
        return true;
      } catch (error) {
        console.error('[ItineraryState] Failed to remove booking details', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to remove booking',
          description: 'Please try again.',
          variant: 'destructive',
        });
        return false;
      }
    },

    /**
     * Cancel a live hotel booking (Sabre PNR) on an itinerary item via the CancelHotelBooking flow.
     * The server cancels + read-back-verifies at Sabre and writes status:'cancelled'; the Firestore
     * listener reflects the new status reactively. Returns true once the booking is cancelled.
     */
    async cancelHotelBooking(itemId: string, reason?: string): Promise<boolean> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot cancel booking',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return false;
      }

      try {
        const { data } = await flows.cancelHotelBooking({
          tripId,
          itineraryItemId: itemId,
          ...(reason ? { reason } : {}),
        });

        if (data.status === 'not_cancellable') {
          toastState.addToast({ title: 'Could not cancel', description: data.reason, variant: 'destructive' });
          return false;
        }

        this.closeViewBookingDialog();
        toastState.addToast({
          title: data.status === 'already_cancelled' ? 'Already cancelled' : 'Booking cancelled',
          description:
            data.status === 'cancelled' && !data.verified
              ? 'Cancellation submitted; awaiting confirmation from the property.'
              : 'Your reservation has been cancelled.',
          variant: 'success',
        });
        return true;
      } catch (error) {
        console.error('[ItineraryState] Failed to cancel hotel booking', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to cancel booking',
          description: 'Please try again or contact support.',
          variant: 'destructive',
        });
        throw error;
      }
    },

    /** Update attachments on a booking itinerary item */
    async updateBookingAttachments(
      itemId: string,
      attachments: Array<{ url: string; name: string; mimeType?: string; size?: number }>,
    ): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      try {
        await flows.updateBookingAttachments({ tripId, itemId, attachments });
      } catch (error) {
        console.error('[ItineraryState] Failed to update booking attachments', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to update attachments',
          description: 'Please try again.',
          variant: 'destructive',
        });
        throw error;
      }
    },

    /** Delete an itinerary item from Firestore */
    async deleteItem(itemId: string, tripId: string) {
      try {
        await flows.deleteItineraryItem({ tripId, itemId });
        trackItineraryItemDeleted({ tripId });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /** Restore an itinerary item */
    async restoreItem(itemId: string, tripId: string) {
      try {
        await flows.restoreItineraryItem({ tripId, itemId });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /**
     * Ensure AI-generated day titles and trip summary exist for the trip's itinerary.
     * Called when the user views the itinerary tab. No-op if version matches; otherwise triggers one agent call.
     * Only sets isGeneratingSummaries when we don't already have summaries, so the UI doesn't show loading when doing a no-op version check.
     */
    async generateSummaries(tripId: string, options?: { hasExistingSummaries?: boolean }): Promise<void> {
      const showLoading = options?.hasExistingSummaries !== true;
      if (showLoading) this.isGeneratingSummaries = true;
      try {
        await flows.generateItinerarySummaries({ tripId });
      } finally {
        if (showLoading) this.isGeneratingSummaries = false;
      }
    },

    /**
     * Synchronously returns the best matching destination for an arrival location.
     * Reads from reactive Firestore queries - returns undefined if data not yet loaded.
     * Uses result caching to avoid repeated computation for the same country code.
     */
    getDestinationForArrival(params: {
      countryCode: string | undefined;
      city?: string | undefined;
    }): Destination | undefined {
      const { countryCode, city } = params;
      if (!countryCode) return undefined;

      try {
        // Create cache key that includes both country code and city for more precise caching
        const cacheKey = city ? `${countryCode}:${city}` : countryCode;

        // Check if we already have a cached result
        if (destinationResultCache.has(cacheKey)) {
          return destinationResultCache.get(cacheKey);
        }

        // Query all destinations for this country
        const query = ClientDestination.list({}, where('location.countryCode', '==', countryCode));

        if (!query.isReady) return undefined;

        const destinations = query.items;
        if (destinations.length === 0) {
          // Cache the undefined result to avoid repeated queries for empty results
          destinationResultCache.set(cacheKey, undefined);
          return undefined;
        }

        let result: Destination | undefined;

        // If we have a city, try to find a destination that matches the city name
        if (city) {
          const cityMatch = destinations.find((dest: Destination) => {
            const destName = dest.name?.toLowerCase();
            const cityLower = city.toLowerCase();
            // Check if destination name contains the city or vice versa
            return destName?.includes(cityLower) || cityLower.includes(destName || '');
          });
          result = cityMatch;
        }

        // Cache the result for future calls
        destinationResultCache.set(cacheKey, result);
        return result;
      } catch (error) {
        getErrorReporter().captureException(error);
        return undefined;
      }
    },

    /**
     * Get a destination document by ID (reactive). Returns undefined until the
     * doc resolves. Used to enrich destination groups with name/country.
     */
    getDestinationById(id: string): Destination | undefined {
      if (!id) return undefined;
      const handle = ClientDestination.get({ id });
      if (!handle.isReady || !handle.data) return undefined;
      return handle.data;
    },

    /**
     * Get a hotel document for a given hotel ID
     * Returns the hotel document if found, else undefined
     */
    getHotel(hotelId: string): Hotel | undefined {
      if (!hotelId) return;

      // Extract just the document ID if a full path was provided
      const docId = hotelId.includes('/') ? hotelId.split('/').pop() : hotelId;
      if (!docId) return;

      const hotelHandle = ClientHotel.get({ id: docId });
      if (!hotelHandle.isReady || !hotelHandle.data) return;

      return hotelHandle.data;
    },
    /** Start monitoring trip changes to clean up stale Firestore listeners (idempotent) */
    startMonitors(): Disposer {
      if (this.cleanupFunctionRef) return this.cleanupFunctionRef;

      const effects = [
        // Unsubscribe old itinerary query when the trip changes
        effect(() => {
          const currentTripId = tripsState.current?.id;
          if (this._previousTripId !== undefined && this._previousTripId !== currentTripId) {
            if (this._prevItemsQuery && this._prevItemsQuery !== EMPTY_ITINERARY_QUERY) {
              this._prevItemsQuery.unsubscribe();
            }
          }
          this._prevItemsQuery = currentTripId ? this.itemsQuery : undefined;
          this._previousTripId = currentTripId;
        }),
      ];

      this.cleanupFunctions.push(...effects);
      this.cleanupFunctionRef = () => this.stopMonitors();
      return this.cleanupFunctionRef;
    },

    /** Clean up all effects to prevent memory leaks */
    stopMonitors(): void {
      if (this._prevItemsQuery && this._prevItemsQuery !== EMPTY_ITINERARY_QUERY) {
        this._prevItemsQuery.unsubscribe();
        this._prevItemsQuery = undefined;
      }
      for (const cleanup of this.cleanupFunctions) cleanup();
      this.cleanupFunctions.length = 0;
      this.cleanupFunctionRef = null;
    },
  });

  return state;
}

export type ItineraryState = ReturnType<typeof createItineraryState>;
